/**
 * Контент сторінки «Цінова політика».
 *
 * До цього все це жило секцією на головній — між «Перевагами» і «Блогом», без
 * власної адреси. Тобто людина, яка шукає «скільки коштує ліцензування
 * медичної практики», не могла ні знайти цю відповідь у пошуку, ні надіслати
 * її комусь посиланням.
 *
 * Жодних сум тут немає навмисно: назвати число, не побачивши документів, у
 * медичному праві означає або перестрахуватися, або пообіцяти те, чого не
 * вийде виконати. Натомість сторінка пояснює, з чого вартість складається і
 * коли її називають.
 */

/**
 * Як з’являється розрахунок. Раніше це був другий абзац вступу, і ті самі
 * слова ще раз стояли у відповіді «Коли я дізнаюся вартість?». Кроками
 * видно головне одразу: ціну називають до початку роботи, а не після.
 */
export const estimateSteps = [
  { title: 'Розмова', text: 'Розповідаєте, на якому ви етапі й що вже готове.' },
  { title: 'Перегляд документів', text: 'Дивимося, що вже є на руках, а що доведеться робити з нуля.' },
  { title: 'Розрахунок', text: 'Називаємо перелік робіт, строк і вартість.' },
  { title: 'Погоджуєте — починаємо', text: 'Знадобиться щось поза переліком — спершу обговоримо це окремо, а не допишемо в рахунок.' }
];

/**
 * Що впливає на вартість. Кожен пункт — «що саме» і «що це означає»:
 * жирна назва читається з першого погляду, пояснення — для тих, хто вчитується.
 */
export const pricingFactors = {
  title: 'Що впливає на вартість',
  // Нерозривні пробіли тримають разом «обсягом роботи» й «назвою послуги»:
  // вирівняний заголовок інакше рвав саме ці пари.
  lead: 'Вартість юридичного супроводу визначається обсягом\u00a0роботи, а\u00a0не назвою\u00a0послуги.',
  example: 'Два заклади можуть звернутися з тим самим запитом — «отримати ліцензію», — і отримати різний розрахунок: в одного приміщення вже відповідає ДБН, а персонал укомплектований, у другого треба міняти планування кабінетів і добирати лікарів із чинними сертифікатами.',
  items: [
    { icon: 'fas fa-file-contract', title: 'Стан документів на старті', text: 'Що вже готове, а що доведеться робити з нуля.' },
    { icon: 'fas fa-building', title: 'Профіль і масштаб закладу', text: 'Кабінет, клініка чи мережа підрозділів.' },
    { icon: 'fas fa-handshake', title: 'Формат', text: 'Разова дія з чітким результатом чи постійний супровід.' },
    { icon: 'fas fa-calendar-check', title: 'Строки', text: 'Чи потрібно вкластися в дату, від якої залежать оренда й персонал.' },
    { icon: 'fas fa-user', title: 'Представництво', text: 'Чи потрібно представляти заклад перед контролюючими органами.' }
  ],
  note: 'Саме ці п’ять пунктів ми з’ясовуємо в першій розмові — після неї розрахунок стає предметним.'
};

/**
 * `pricing` — як рахується формат. Цифр немає, але на місці, де на
 * сторінках цін зазвичай стоїть сума, людина бачить, за що саме платить.
 */
export const formats = [
  {
    title: 'Разові послуги',
    pricing: 'Фіксована вартість за результат',
    text: 'Реєстрація бізнесу, отримання ліцензії, підготовка комплекту документації.',
    fit: 'Підходить, коли завдання одне й має чітку межу.',
    icon: 'fas fa-arrows-to-circle'
  },
  {
    title: 'Абонентське обслуговування',
    pricing: 'Щомісячна оплата',
    text: 'Комплексний юридичний супровід: договірна робота, консультації, представництво в органах.',
    fit: 'Підходить чинному закладу, у якого юридичні питання виникають постійно.',
    icon: 'fas fa-id-card'
  },
  {
    title: 'Індивідуальні пакети',
    pricing: 'За складом пакета',
    text: 'Формуються під потреби конкретної клініки чи лікаря з урахуванням специфіки діяльності.',
    fit: 'Підходить, коли завдань кілька і вони розтягнуті в часі — як при відкритті клініки.',
    icon: 'fas fa-sitemap'
  }
];

export const principles = [
  {
    title: 'Прозорість',
    text: 'Клієнт завжди знає, за що платить. Жодних прихованих платежів чи «дрібного шрифту».'
  },
  {
    title: 'Гнучкість',
    text: 'Ми пропонуємо різні формати співпраці — від разових консультацій до комплексного абонентського супроводу.'
  },
  {
    title: 'Доступність',
    text: 'Якісний юридичний сервіс має бути інвестицією у безпеку бізнесу, а не непосильним фінансовим тягарем.'
  }
];

export const clientBenefit =
  'Ви отримуєте прогнозовані витрати та впевненість у тому, що всі юридичні питання будуть вирішені професійно\u00a0та\u00a0вчасно.';

export const pricingFaq = [
  {
    q: 'Чому на сайті немає прайс-листа?',
    a: 'Тому що однакові за назвою завдання відрізняються за обсягом у рази. «Підготувати документи для ліцензії» для закладу, у якого приміщення вже відповідає нормам, а персонал укомплектований, і для того, хто починає з порожнього приміщення, — це різна робота. Цифра на сайті в такому разі або завищена «про всяк випадок», або занижена й перестає діяти після першої ж зустрічі.'
  },
  {
    q: 'Коли я дізнаюся вартість?',
    a: 'До початку роботи. Спершу ми з’ясовуємо ситуацію та дивимося наявні документи, потім називаємо перелік робіт, строк і вартість. Погоджуєте — починаємо.'
  },
  {
    q: 'Чим разова послуга відрізняється від абонентського обслуговування?',
    a: 'Разова послуга має результат і межу: ліцензія отримана, документи готові, торговельну марку подано на реєстрацію. Абонентське обслуговування — це постійна присутність юриста в поточних питаннях закладу: договори, консультації, перевірки, листування з органами. Заклад, що відкривається, зазвичай починає з разових послуг і переходить на супровід після старту роботи.'
  },
  {
    q: 'Що буде, якщо обсяг робіт зміниться в процесі?',
    a: 'Ми повідомляємо про це до того, як щось робити, і узгоджуємо окремо. Роботи, про які ви не знали й на які не погоджувалися, у рахунку не з’являються — це і є та прозорість, про яку йдеться вище.'
  }
];
